<app-header />

@if (isLoading) {
  <div class="max-w-7xl mx-auto px-6 py-20 text-center">
    <p class="text-gray-600">Cargando actividad...</p>
  </div>
} @else if (error || !event || !subevento) {
  <div class="max-w-7xl mx-auto px-6 py-20 text-center">
    <h2 class="text-2xl font-bold text-gray-800 mb-4">Actividad no encontrada</h2>
    <p class="text-gray-600 mb-6">La actividad que buscas no existe o no está disponible.</p>
    <button
      (click)="goBack()"
      class="bg-[#e8343f] text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors"
    >
      Volver al evento
    </button>
  </div>
} @else {

  <!-- Banner -->
  <section class="border-b border-gray-300">
    <div class="max-w-7xl mx-auto px-6 pt-2 pb-4">
      <div class="aspect-[40/21] bg-gray-100 border border-gray-300 overflow-hidden rounded-2xl relative">
        <img
          [src]="getHorizontalImage()"
          [alt]="subevento.nombre"
          class="w-full h-full object-cover"
        />
        <!-- Overlay con gradiente -->
        <div class="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-8">
          <!-- Breadcrumb sobre el banner -->
          <div>
            <nav class="flex items-center text-white/80 text-sm mb-3 flex-wrap gap-1">
              <button (click)="goBack()" class="hover:text-white transition-colors">{{ event.nombre }}</button>
              <svg class="w-4 h-4 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
              </svg>
              <span class="text-white font-semibold">{{ subevento.nombre }}</span>
            </nav>
            <h1 class="text-3xl md:text-4xl font-bold text-white">{{ subevento.nombre }}</h1>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Contenido principal -->
  <section class="py-10">
    <div class="max-w-7xl mx-auto px-6">
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-10">

        <!-- Columna izquierda: Info de la actividad -->
        <div class="space-y-6">
          <!-- Breadcrumb para móvil -->
          <nav class="flex items-center text-gray-500 text-sm lg:hidden flex-wrap gap-1">
            <button (click)="goBack()" class="hover:text-[#e8343f] transition-colors">{{ event.nombre }}</button>
            <svg class="w-4 h-4 mx-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
            <span class="text-gray-800 font-semibold">{{ subevento.nombre }}</span>
          </nav>

          <!-- <h2 class="text-2xl font-bold text-gray-900">Sobre esta actividad</h2> -->

          @if (subevento.contenido_html) {
            <div
              class="prose prose-sm max-w-none text-gray-700"
              [innerHTML]="sanitizeHtml(subevento.contenido_html)"
            ></div>
          } @else if (subevento.descripcion) {
            <p class="text-gray-700 leading-relaxed">{{ subevento.descripcion }}</p>
          }

          <!-- Botón volver -->
          <div class="pt-4">
            <button
              (click)="goBack()"
              class="flex items-center text-gray-600 hover:text-[#e8343f] transition-colors"
            >
              <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
              Volver al evento
            </button>
          </div>
        </div>

        <!-- Columna derecha: Calendario -->
        <div>
          <h2 class="text-2xl font-bold text-gray-900 mb-6">Selecciona una fecha</h2>

          @if (isLoadingFechas) {
            <div class="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <p class="text-gray-500">Cargando fechas disponibles...</p>
            </div>
          } @else {
            <div class="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">

              <!-- Navegación del calendario -->
              <div class="flex items-center justify-between mb-6">
                <button
                  (click)="prevMonth()"
                  class="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                  aria-label="Mes anterior"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
                  </svg>
                </button>
                <span class="text-lg font-semibold text-gray-800 capitalize">{{ monthLabel }}</span>
                <button
                  (click)="nextMonth()"
                  class="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                  aria-label="Mes siguiente"
                >
                  <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
                  </svg>
                </button>
              </div>

              <!-- Días de la semana -->
              <div class="grid grid-cols-7 mb-2">
                @for (dow of calendarWeekDays; track dow) {
                  <div class="text-center text-xs font-semibold text-gray-500 py-1">{{ dow }}</div>
                }
              </div>

              <!-- Días del calendario -->
              <div class="grid grid-cols-7 gap-1">
                @for (day of calendarDays; track day.date) {
                  <button
                    (click)="selectDate(day)"
                    [disabled]="!day.available || day.isPast || !day.isCurrentMonth"
                    class="aspect-square rounded-lg text-sm font-medium transition-all flex items-center justify-center"
                    [class.text-gray-300]="!day.isCurrentMonth"
                    [class.text-gray-400]="day.isCurrentMonth && day.isPast && !day.available"
                    [class.cursor-not-allowed]="!day.available || day.isPast || !day.isCurrentMonth"
                    [class.bg-[#e8343f]]="day.available && !day.isPast && day.isCurrentMonth"
                    [class.text-white]="day.available && !day.isPast && day.isCurrentMonth"
                    [class.hover:bg-red-700]="day.available && !day.isPast && day.isCurrentMonth"
                    [class.cursor-pointer]="day.available && !day.isPast && day.isCurrentMonth"
                    [class.ring-2]="day.isToday"
                    [class.ring-[#e8343f]]="day.isToday"
                    [class.font-bold]="day.isToday"
                    [class.text-gray-800]="day.isCurrentMonth && !day.available && !day.isPast"
                    [class.hover:bg-gray-100]="day.isCurrentMonth && !day.available && !day.isPast"
                  >
                    {{ day.day }}
                  </button>
                }
              </div>

              <!-- Leyenda -->
              <div class="mt-6 flex flex-wrap gap-4 text-xs text-gray-600 border-t border-gray-100 pt-4">
                <div class="flex items-center gap-2">
                  <span class="w-4 h-4 rounded-md bg-[#e8343f] inline-block"></span>
                  Fecha disponible
                </div>
                <div class="flex items-center gap-2">
                  <span class="w-4 h-4 rounded-md bg-gray-200 inline-block"></span>
                  No disponible
                </div>
              </div>

              @if (availableFechas.length === 0) {
                <div class="mt-4 text-center text-gray-500 text-sm">
                  No hay fechas disponibles para esta actividad.
                </div>
              }
            </div>
          }
        </div>

      </div>
    </div>
  </section>
}

<app-footer />
<app-scroll-to-top />
